import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, TextField, Tooltip, Typography,
} from "@mui/material";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { toast } from "react-toastify";
import {
  deleteInternalDocument,
  downloadInternalDocument,
  listAgreementTemplates,
  listTenantDocuments,
  uploadAgreementTemplate,
  uploadTenantDocument,
} from "./api/internalDocumentsApi";

const INK = "#0B2A4A", BLUE = "#1E6FE8", MUTED = "#64748b", LINE = "#e3ebf3";
const TEMPLATE_CATEGORIES = [
  ["client_agreement", "Client agreement"], ["partner_agreement", "Partner agreement"],
  ["nda", "NDA"], ["dpa", "Data processing agreement"], ["other", "Other"],
];
const TENANT_CATEGORIES = [
  ["contract", "Contract"], ["quote", "Quote"], ["kyb", "KYB"], ["nda", "NDA"],
  ["compliance", "Compliance"], ["invoice", "Invoice"], ["other", "Other"],
];
const formatBytes = (bytes) => {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};
const formatDate = (value) => value
  ? new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
  : "—";

export default function InternalDocumentsFolder({ tenantId, tenantName, embedded = false }) {
  const templates = !tenantId;
  const categories = templates ? TEMPLATE_CATEGORIES : TENANT_CATEGORIES;
  const inputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(categories[0][0]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const rows = templates ? await listAgreementTemplates() : await listTenantDocuments(tenantId);
      setDocuments(Array.isArray(rows) ? rows : []);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || requestError?.message || "Could not load documents");
    } finally { setLoading(false); }
  }, [templates, tenantId]);

  useEffect(() => { load(); }, [load]);

  const labels = useMemo(() => Object.fromEntries(categories), [categories]);
  const chooseFile = (selected) => {
    if (!selected) return;
    if (selected.size > 25 * 1024 * 1024) return toast.error("Files must be 25 MB or smaller");
    setFile(selected); setTitle(selected.name.replace(/\.[^.]+$/, "")); setUploadOpen(true);
  };
  const closeUpload = (force = false) => {
    if (saving && !force) return;
    setUploadOpen(false); setFile(null); setTitle(""); setCategory(categories[0][0]);
    if (inputRef.current) inputRef.current.value = "";
  };
  const submit = async () => {
    if (!file || !title.trim()) return;
    setSaving(true);
    try {
      if (templates) await uploadAgreementTemplate(file, { title: title.trim(), category });
      else await uploadTenantDocument(tenantId, file, { title: title.trim(), category });
      toast.success("Document uploaded"); closeUpload(true); await load();
    } catch (requestError) {
      toast.error(requestError?.response?.data?.error || requestError?.message || "Upload failed");
    } finally { setSaving(false); }
  };
  const download = async (document) => {
    try {
      const blob = await downloadInternalDocument(document.id);
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = document.originalFilename || document.title || "document";
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (requestError) { toast.error(requestError?.response?.data?.error || "Download failed"); }
  };
  const remove = async (document) => {
    if (!window.confirm(`Delete “${document.title || document.originalFilename}”? This cannot be undone.`)) return;
    try {
      await deleteInternalDocument(document.id);
      setDocuments((rows) => rows.filter((row) => row.id !== document.id));
      toast.success("Document deleted");
    } catch (requestError) { toast.error(requestError?.response?.data?.error || "Delete failed"); }
  };

  return (
    <Box data-track-area={templates ? "Agreement templates folder" : "Tenant documents folder"} sx={{ ...(embedded ? {} : { p: { xs: 2, md: 2.5 } }) }}>
      <input ref={inputRef} hidden type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg" onChange={(event) => chooseFile(event.target.files?.[0])} />
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box sx={{ width: 42, height: 42, borderRadius: "11px", bgcolor: "#eef5ff", color: BLUE, display: "grid", placeItems: "center" }}><FolderOutlinedIcon /></Box>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: INK }}>{templates ? "All agreement templates" : `${tenantName || "Tenant"} documents`}</Typography>
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>{documents.length} document{documents.length === 1 ? "" : "s"} · private to Shimon and Michal</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<UploadFileOutlinedIcon />} onClick={() => inputRef.current?.click()}
          sx={{ textTransform: "none", fontWeight: 750, borderRadius: "10px", bgcolor: BLUE }}>Upload document</Button>
      </Box>

      {loading && <Box role="status" sx={{ py: 5, display: "flex", justifyContent: "center" }}><CircularProgress size={24} /></Box>}
      {!loading && error && <Alert severity="warning" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{error}</Alert>}
      {!loading && !error && documents.length === 0 && (
        <Box sx={{ border: `1px dashed #cbd8e6`, borderRadius: "13px", textAlign: "center", py: 5, px: 2, bgcolor: "#fbfdff" }}>
          <FolderOutlinedIcon sx={{ fontSize: 34, color: "#a8b7c7", mb: .75 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 750, color: INK }}>This folder is ready</Typography>
          <Typography sx={{ fontSize: 12.5, color: MUTED, mt: .4 }}>{templates ? "Upload the agreement templates when their location is provided." : "Add the contract, quote, KYB, and other relevant account files."}</Typography>
        </Box>
      )}
      {!loading && !error && documents.length > 0 && (
        <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "13px", overflow: "hidden" }}>
          {documents.map((document, index) => (
            <Box key={document.id} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr auto", md: "minmax(240px,1.7fr) minmax(120px,.7fr) 100px 115px 84px" }, gap: 1, alignItems: "center", px: 1.75, py: 1.35, borderTop: index ? `1px solid ${LINE}` : "none" }}>
              <Box sx={{ display: "flex", gap: 1.1, alignItems: "center", minWidth: 0 }}>
                <DescriptionOutlinedIcon sx={{ color: BLUE, fontSize: 21 }} />
                <Box sx={{ minWidth: 0 }}><Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{document.title || document.originalFilename}</Typography><Typography sx={{ fontSize: 11.5, color: MUTED }}>{document.originalFilename}</Typography></Box>
              </Box>
              <Chip size="small" label={labels[document.category] || "Other"} sx={{ display: { xs: "none", md: "inline-flex" }, justifySelf: "start", height: 23, fontSize: 10.5 }} />
              <Typography sx={{ display: { xs: "none", md: "block" }, fontSize: 12, color: MUTED }}>{formatBytes(document.fileSize)}</Typography>
              <Typography sx={{ display: { xs: "none", md: "block" }, fontSize: 12, color: MUTED }}>{formatDate(document.createdAt)}</Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Tooltip title="Download"><IconButton size="small" onClick={() => download(document)} aria-label={`Download ${document.title}`}><DownloadOutlinedIcon sx={{ fontSize: 19 }} /></IconButton></Tooltip>
                <Tooltip title="Delete"><IconButton size="small" onClick={() => remove(document)} aria-label={`Delete ${document.title}`}><DeleteOutlineIcon sx={{ fontSize: 19 }} /></IconButton></Tooltip>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={uploadOpen} onClose={closeUpload} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800, color: INK }}>Upload document</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: "12px !important" }}>
          <Alert severity="info" icon={<DescriptionOutlinedIcon />}>{file?.name} · {formatBytes(file?.size)}</Alert>
          <TextField label="Document title" value={title} onChange={(event) => setTitle(event.target.value)} autoFocus inputProps={{ maxLength: 240 }} />
          <TextField select label="Document type" value={category} onChange={(event) => setCategory(event.target.value)}>
            {categories.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeUpload} disabled={saving} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !title.trim()} variant="contained" sx={{ textTransform: "none", fontWeight: 700 }}>{saving ? <CircularProgress size={18} color="inherit" /> : "Upload"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
